import React, { useContext, useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import cn from 'classnames';
import ConfigContext from '../../../contexts/ConfigContext';
import useIsMobile from '../../../hooks/useIsMobile';
import { fixBodyScroll, unfixBodyScroll } from '../../../modules/helpers';

// Components
import MobileMenu from './MobileMenu';
import MobileSearch from './MobileSearch';
import TermSearch from './TermSearch';
import FoodTrucksMenu from './FoodTrucksMenu';
import CateringMenu from './CateringMenu';
import HeaderNavControls from './HeaderNavControls';

import styles from './Header.module.postcss';

function Header() {
  const { city_name, logo, site_title, vehicle_type } = useContext(ConfigContext);
  const isMobile = useIsMobile(1080);
  const location = useLocation();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [isClient, setIsClient] = useState(false);

  // Desktop mega-menu state
  const [openMenu, setOpenMenu] = useState(null); // 'foodTrucks', 'catering', 'services', or null
  const closeTimerRef = useRef(null);

  useEffect(() => {
    setIsClient(true);
  }, []);

  // Close menus on navigation
  useEffect(() => {
    unfixBodyScroll(scrollY);
    setMobileMenuOpen(false);
    setMobileSearchOpen(false);
    setOpenMenu(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  // Close mobile menu when switching to desktop
  useEffect(() => {
    if (!isMobile && mobileMenuOpen) {
      unfixBodyScroll(scrollY);
      setMobileMenuOpen(false);
      setMobileSearchOpen(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);

  const toggleMenu = () => {
    if (mobileMenuOpen) {
      unfixBodyScroll(scrollY);
      setMobileMenuOpen(false);
    } else {
      const newScrollY = fixBodyScroll();
      setScrollY(newScrollY);
      setMobileMenuOpen(true);
    }
    setMobileSearchOpen(false);
  };

  const toggleSearch = () => {
    if (mobileSearchOpen) {
      unfixBodyScroll(scrollY);
      setMobileSearchOpen(false);
    } else {
      const newScrollY = fixBodyScroll();
      setScrollY(newScrollY);
      setMobileSearchOpen(true);
    }
    setMobileMenuOpen(false);
  };

  // Desktop hover handlers with close delay
  const handleMenuEnter = useCallback((menuName) => {
    clearTimeout(closeTimerRef.current);
    setOpenMenu(menuName);
  }, []);

  const handleMenuLeave = useCallback(() => {
    closeTimerRef.current = setTimeout(() => {
      setOpenMenu(null);
    }, 200);
  }, []);

  const handleMenuClick = useCallback((menuName) => {
    setOpenMenu((prev) => (prev === menuName ? null : menuName));
  }, []);

  return (
    <header className={styles.Header}>
      <div className='Section'>

        <div className={styles.Header_Left}>
          <Link to='/'>
            <img className={styles.Header_img} src={logo} alt={site_title} width='180' height='30' />
          </Link>

          <TermSearch
            className={styles.Header_noMobileFlex}
          />
        </div>

        <nav className={styles.Header_Right}>

          {/* Food Trucks mega-menu trigger */}
          <div
            className={styles.Header_noMobile}
            onMouseEnter={() => handleMenuEnter('foodTrucks')}
            onMouseLeave={handleMenuLeave}
          >
            <button
              type='button'
              className={cn(styles.Header_Link, styles.Header_NavTrigger)}
              onClick={() => handleMenuClick('foodTrucks')}
            >
              Food {vehicle_type}s
            </button>
          </div>

          {/* Catering mega-menu trigger */}
          <div
            className={styles.Header_noMobile}
            onMouseEnter={() => handleMenuEnter('catering')}
            onMouseLeave={handleMenuLeave}
          >
            <button
              type='button'
              className={cn(styles.Header_Link, styles.Header_NavTrigger)}
              onClick={() => handleMenuClick('catering')}
            >
              Catering
            </button>
          </div>

          {/* Services dropdown trigger */}
          <div
            className={styles.Header_noMobile}
            style={{ position: 'relative' }}
            onMouseEnter={() => handleMenuEnter('services')}
            onMouseLeave={handleMenuLeave}
          >
            <button
              type='button'
              className={cn(styles.Header_Link, styles.Header_NavTrigger)}
              onClick={() => handleMenuClick('services')}
            >
              Services
            </button>

            {/* Services dropdown — always in DOM */}
            <div
              className={cn(styles.ServicesDropdownWrapper, {
                [styles.ServicesDropdownWrapper__open]: openMenu === 'services',
              })}
              aria-hidden={openMenu !== 'services'}
            >
              <Link to='/services/office-parks'>Office Parks</Link>
              <Link to='/services/residential'>Residential</Link>
              { city_name !== 'Portland' &&
                <Link to='/services/breweries'>Breweries</Link>
              }
            </div>
          </div>

          <HeaderNavControls />

          <button
            type='button'
            className={cn(styles.HeaderMenuToggle, styles.Header_mobileOnly)}
            onClick={toggleSearch}
          >
            { !mobileSearchOpen && <img src='/static/images/admin/search-black.svg' alt='Toggle Search' width='16' height='16' />}
            { mobileSearchOpen && <img src='/static/images/admin/x-black.svg' alt='Toggle Search' width='16' height='16' />}
          </button>

          <button
            type='button'
            className={cn(styles.HeaderMenuToggle, styles.Header_mobileOnly)}
            onClick={toggleMenu}
          >
            { !mobileMenuOpen && <img src='/static/images/admin/navigation/menu.svg' alt='Toggle Menu' width='16' height='16' /> }
            { mobileMenuOpen && <img src='/static/images/admin/x-black.svg' alt='Toggle Menu' width='16' height='16' /> }
          </button>
        </nav>

      </div>

      {/* Food Trucks mega-menu — always in DOM for SEO */}
      <div
        className={cn(styles.MegaMenuWrapper, {
          [styles.MegaMenuWrapper__open]: openMenu === 'foodTrucks',
        })}
        aria-hidden={openMenu !== 'foodTrucks'}
        onMouseEnter={() => handleMenuEnter('foodTrucks')}
        onMouseLeave={handleMenuLeave}
      >
        <div className='Section'>
          <FoodTrucksMenu cityName={city_name} vehicleType={vehicle_type} />
        </div>
      </div>

      {/* Catering mega-menu — always in DOM for SEO */}
      <div
        className={cn(styles.MegaMenuWrapper, {
          [styles.MegaMenuWrapper__open]: openMenu === 'catering',
        })}
        aria-hidden={openMenu !== 'catering'}
        onMouseEnter={() => handleMenuEnter('catering')}
        onMouseLeave={handleMenuLeave}
      >
        <div className='Section'>
          <CateringMenu />
        </div>
      </div>

      { isClient && isMobile &&
        <div
          className={cn(styles.MobileMenu_Backdrop, {
            [styles.MobileMenu_Backdrop__visible]: mobileMenuOpen,
          })}
          onClick={toggleMenu}
        />
      }

      { isClient &&
        <MobileMenu
          mobileMenuOpen={mobileMenuOpen}
          cityName={city_name}
          vehicleType={vehicle_type}
        />
      }

      { isClient && isMobile &&
        <MobileSearch
          mobileSearchOpen={mobileSearchOpen}
        />
      }
    </header>
  );
}

export default Header;
